'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import { KEEPING_GREEN, PROOF, RACING, TECH_HEADING, TECH_LEAD, TECH_SECTIONS } from '@/data/flyerContent';

// Sections built from the Green Filter brochure (2026 CD flyer). Content lives in data/flyerContent.ts.

export function RacingBand({ hideButton }: { hideButton?: boolean }) {
  return (
    <DarkSection>
      <RacingGrid>
        <div>
          <Eyebrow>{RACING.eyebrow}</Eyebrow>
          <DarkHeading>{RACING.heading}</DarkHeading>
          <Bar />
          <DarkText>{RACING.text}</DarkText>
          <Highlight>{RACING.highlight}</Highlight>
          <Tagline>{RACING.tagline}</Tagline>
          {!hideButton && <LightButton href={RACING.buttonLink}>{RACING.buttonLabel}</LightButton>}
        </div>
        <RacingImage>
          <Image src={RACING.image} alt={RACING.alt} fill sizes="(max-width: 991px) 100vw, 560px" />
        </RacingImage>
      </RacingGrid>
    </DarkSection>
  );
}

/** The big numbers. `detailed` adds each number's explanation and the horsepower bars (Why Go Green). */
export function ProofStats({ detailed }: { detailed?: boolean }) {
  const { dyno } = PROOF;
  const max = Math.max(dyno.greenHp, dyno.stockHp);
  return (
    <ProofSection $dark={!detailed}>
      <Container>
        <CenteredHeading $dark={!detailed}>{PROOF.heading}</CenteredHeading>
        <Bar $center />
        <Lead $dark={!detailed}>{PROOF.lead}</Lead>
        <StatGrid>
          {PROOF.stats.map((s) => (
            <Stat key={s.label} $dark={!detailed}>
              <StatValue $dark={!detailed}>{s.value}</StatValue>
              <StatLabel>{s.label}</StatLabel>
              {detailed && <StatText>{s.text}</StatText>}
            </Stat>
          ))}
        </StatGrid>
        {detailed && (
          <Dyno>
            <DynoTitle>{dyno.title}</DynoTitle>
            {[
              { label: 'Green Filter', hp: dyno.greenHp, green: true },
              { label: 'Stock paper filter', hp: dyno.stockHp, green: false },
            ].map((b) => (
              <DynoRow key={b.label}>
                <strong>{b.label}</strong>
                <DynoTrack>
                  <DynoBar $green={b.green} style={{ width: `${(b.hp / max) * 100}%` }}>
                    {b.hp} hp
                  </DynoBar>
                </DynoTrack>
              </DynoRow>
            ))}
            <Note>{dyno.note}</Note>
          </Dyno>
        )}
      </Container>
    </ProofSection>
  );
}

export function TechStory() {
  return (
    <MutedSection>
      <Container>
        <CenteredHeading $dark={false}>{TECH_HEADING}</CenteredHeading>
        <Bar $center />
        <Lead $dark={false}>{TECH_LEAD}</Lead>
      </Container>
      <TechList>
        {TECH_SECTIONS.map((s, i) => (
          <TechRow key={s.title} $flip={i % 2 === 1}>
            <TechImage $flip={i % 2 === 1}>
              <TechImageInner>
                <Image src={s.image} alt={s.alt} fill sizes="(max-width: 991px) 100vw, 480px" />
              </TechImageInner>
            </TechImage>
            <div>
              <TechStep aria-hidden="true">{String(i + 1).padStart(2, '0')}</TechStep>
              <TechTitle>{s.title}</TechTitle>
              <TechText>{s.text}</TechText>
              {s.points && (
                <Points>
                  {s.points.map((p) => (
                    <li key={p.title}>
                      <PointTitle>{p.title}</PointTitle>
                      <p>{p.text}</p>
                    </li>
                  ))}
                </Points>
              )}
            </div>
          </TechRow>
        ))}
      </TechList>
    </MutedSection>
  );
}

export function KeepingGreen() {
  return (
    <WhiteSection>
      <KeepingCard>
        <KeepingTitle>{KEEPING_GREEN.heading}</KeepingTitle>
        <KeepingText>{KEEPING_GREEN.text}</KeepingText>
        <Note>{KEEPING_GREEN.note}</Note>
      </KeepingCard>
    </WhiteSection>
  );
}

const sectionPadding = `
  padding: 80px 24px;

  @media (max-width: 1024px) {
    padding: 64px 24px;
  }

  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const DarkSection = styled.section`
  background: #111;
  ${sectionPadding}
`;

const MutedSection = styled.section`
  background: #f8f8f8;
  ${sectionPadding}
`;

const WhiteSection = styled.section`
  background: #fff;
  ${sectionPadding}
`;

const ProofSection = styled.section<{ $dark: boolean }>`
  background: ${(p) => (p.$dark ? '#111' : '#fff')};
  ${sectionPadding}
`;

const Container = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const RacingGrid = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const Eyebrow = styled.p`
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${COLORS.brandGreen};
  margin: 0 0 10px;
`;

const DarkHeading = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 36px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 14px;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 28px;
  }
`;

const CenteredHeading = styled(DarkHeading)<{ $dark: boolean }>`
  color: ${(p) => (p.$dark ? '#fff' : '#1a1a1a')};
  text-align: center;
`;

const Bar = styled.div<{ $center?: boolean }>`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: ${(p) => (p.$center ? '0 auto 20px' : '0 0 20px')};
`;

const DarkText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.65;
  margin: 0;
`;

const Lead = styled.p<{ $dark: boolean }>`
  font-family: Lato, sans-serif;
  font-size: 17px;
  color: ${(p) => (p.$dark ? 'rgba(255, 255, 255, 0.85)' : '#555')};
  text-align: center;
  max-width: 720px;
  margin: 0 auto 36px;
  line-height: 1.6;
`;

const Highlight = styled.p`
  font-family: Lato, sans-serif;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  color: #fff;
  border-left: 4px solid ${COLORS.brandGreen};
  padding-left: 16px;
  margin: 24px 0 8px;

  @media (max-width: 767px) {
    font-size: 21px;
  }
`;

const Tagline = styled.p`
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
  font-style: italic;
  color: ${COLORS.brandGreen};
  margin: 0 0 24px;
`;

const LightButton = styled(Link)`
  display: inline-block;
  padding: 14px 28px;
  border-radius: 6px;
  background: #fff;
  color: #111;
  font-family: Lato, sans-serif;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    background: ${COLORS.brandGreen};
    color: #fff;
  }
`;

const RacingImage = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);

  img {
    object-fit: cover;
  }
`;

const StatGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.li<{ $dark: boolean }>`
  padding: 24px;
  border-radius: 16px;
  text-align: center;
  background: ${(p) => (p.$dark ? 'rgba(255, 255, 255, 0.06)' : '#fff')};
  color: ${(p) => (p.$dark ? '#fff' : '#1a1a1a')};
  border: 1px solid ${(p) => (p.$dark ? 'rgba(255, 255, 255, 0.12)' : '#ebebeb')};
  border-top: 4px solid ${COLORS.brandGreen};
`;

const StatValue = styled.span<{ $dark: boolean }>`
  display: block;
  font-family: Lato, sans-serif;
  font-size: 40px;
  font-weight: 800;
  line-height: 1;
  color: ${(p) => (p.$dark ? COLORS.brandGreen : COLORS.brandGreenAccessible)};
`;

const StatLabel = styled.span`
  display: block;
  margin-top: 8px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 800;
  text-transform: uppercase;
`;

const StatText = styled.p`
  margin: 12px 0 0;
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  text-align: left;
`;

const Dyno = styled.figure`
  max-width: 720px;
  margin: 40px auto 0;
`;

const DynoTitle = styled.figcaption`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 800;
  font-style: italic;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const DynoRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 16px;
  align-items: center;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const DynoTrack = styled.span`
  display: block;
  background: #f0f0f0;
  border-radius: 8px;
`;

const DynoBar = styled.span<{ $green: boolean }>`
  display: block;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${(p) => (p.$green ? COLORS.brandGreen : '#e8701a')};
  color: #fff;
  font-weight: 800;
  text-align: right;
`;

const Note = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  font-style: italic;
  color: #8a8a8a;
`;

const TechList = styled(Container)`
  display: grid;
  gap: 72px;
  margin-top: 16px;
`;

const TechRow = styled.article<{ $flip: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$flip ? '7fr 5fr' : '5fr 7fr')};
  gap: 40px;
  align-items: center;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const TechImage = styled.div<{ $flip: boolean }>`
  padding: 24px;
  border: 1px solid #ebebeb;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.08);
  order: ${(p) => (p.$flip ? 2 : 0)};

  @media (max-width: 991px) {
    order: 0;
    max-width: 420px;
    width: 100%;
  }
`;

const TechImageInner = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;

  img {
    object-fit: contain;
  }
`;

const TechStep = styled.span`
  display: block;
  margin-bottom: 8px;
  font-family: Lato, sans-serif;
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
  color: ${COLORS.brandGreen};
`;

const TechTitle = styled.h3`
  text-align: left;
  font-family: Lato, sans-serif;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  margin: 0 0 12px;
  color: #1a1a1a;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const TechText = styled.p`
  font-size: 16px;
  line-height: 1.65;
  color: #555;
  margin: 0;
`;

const Points = styled.ul`
  list-style: none;
  margin: 24px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  li {
    padding: 16px;
    border-left: 4px solid ${COLORS.brandGreen};
    border-radius: 8px;
    background: #fff;
  }

  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: #555;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PointTitle = styled.h4`
  text-align: left;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 800;
  text-transform: uppercase;
  margin: 0 0 4px;
  color: #1a1a1a;
`;

const KeepingCard = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 32px;
  border-radius: 16px;
  background: rgba(0, 173, 35, 0.12);
  border-left: 6px solid ${COLORS.brandGreen};

  @media (max-width: 767px) {
    padding: 24px;
  }
`;

const KeepingTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 30px;
  font-weight: 800;
  text-transform: uppercase;
  color: ${COLORS.brandGreenAccessible};
  margin: 0 0 12px;
`;

const KeepingText = styled.p`
  font-size: 17px;
  line-height: 1.65;
  color: #1a1a1a;
  margin: 0;
`;
